import React, { useState } from "react";
import { Box, Paper, Typography, Fade, Grid, Button } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ShippingModeSelector from "./ShippingModeSelector";
import PickupAddressForm from "./PickupAddressForm";
import DeliveryAddressForm from "./DeliveryAddressForm";
import BillingAddressForm from "./BillingAddressForm";
import theme from "../../../styles/theme/theme";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImportantNotes from "./ImportantNotes";
import AuthButton from "../../../components/auth/AuthButton";
import {
  billingSchema,
  deliverySchema,
  pickupSchema,
  transportDocumentsSchema,
} from "./addressSchemas";
import TransportDocumentsForm from "./TransportDocumentsForm";
import { useDispatch, useSelector } from "react-redux";
import { buildShippingFormData } from "./buildShippingFormData";
import { orderShippingDetails } from "../../../features/buyer/buyerSlice";

const muiTheme = createTheme(theme);

const defaultAddress = () => ({
  // Common fields for Pickup, Delivery, and Billing
  firstName: "",
  lastName: "",
  phone: "",
  companyName: "",
  country: "",
  state: "",
  city: "",
  postcode: "",
  addressLine1: "",
  addressLine2: "",
  landmark: "",
  email: "",
  locationType: "",
  forkliftAvailability: false,
  assistanceAvailable: false,
  startDate: "",
  endDate: "",
  images: [],
  documents: [],
  deliveryTimeSlot: "",
  deliveryNotes: "",
  pickupTimeSlot: "",
  pickupNotes: "",
  transportDocuments: {
    deliveryNote: [],
    packingList: [],
    weightSlip: [],
    invoice: [],
    consignmentNote: [],
    manifest: [],
  },
  vehicleType: "",
  registrationNo: "",
  driverName: "",
  driverPhone: "",
  confirmationNumber: "",
  carrier: "",
  conNote: "",
});

export default function ShippingTabsCard() {
  const [mode, setMode] = useState("self");
  const [billing, setBilling] = useState(defaultAddress());
  const [pickup, setPickup] = useState(defaultAddress());
  const [delivery, setDelivery] = useState(defaultAddress());
  const [transport, setTransport] = useState(defaultAddress());
  const [errors, setErrors] = useState({
    billing: {},
    pickup: {},
    delivery: {},
    transport: {},
  });

  const dispatch = useDispatch();
  const { orderShippingLoading, orderShippingError, orderShippingSuccess } =
    useSelector((state) => state.buyer);

  const handleChange = (setter, formName) => (field) => (e) => {
    const value = e?.target?.value ?? e;
    setter((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({
      ...prev,
      [formName]: {
        ...prev[formName],
        [field]: "",
      },
    }));
  };

  const handleToggle = (setter) => (field) => (e) => {
    const value = e.target?.checked ?? false;
    setter((prev) => ({ ...prev, [field]: value }));
  };

  const required = (value) =>
    value === undefined || value === null || value === ""
      ? "Required field"
      : "";

  const showPickup = mode !== "seller";
  const showDelivery = mode === "third" || mode === "seller";

  const submit = async () => {
    const validations = [];
    const collectedErrors = {
      billing: {},
      pickup: {},
      delivery: {},
      transport: {},
    };

    // MODE: self
    if (mode === "self") {
      validations.push(
        {
          name: "pickup",
          data: pickup,
          schema: pickupSchema,
          mapPath: (p) => p,
        },
        {
          name: "billing",
          data: billing,
          schema: billingSchema,
          mapPath: (p) => p,
        }
      );
    }

    // MODE: third
    if (mode === "third") {
      validations.push(
        {
          name: "pickup",
          data: pickup,
          schema: pickupSchema,
          mapPath: (p) => p,
        },
        {
          name: "delivery",
          data: delivery,
          schema: deliverySchema,
          mapPath: (p) => p,
        },
        {
          name: "transport",
          data: transport,
          schema: transportDocumentsSchema,
          mapPath: (p) => `documents.${p}`,
        },
        {
          name: "billing",
          data: billing,
          schema: billingSchema,
          mapPath: (p) => p,
        }
      );
    }

    // MODE: seller
    if (mode === "seller") {
      validations.push(
        {
          name: "delivery",
          data: delivery,
          schema: deliverySchema,
          mapPath: (p) => p,
        },
        {
          name: "billing",
          data: billing,
          schema: billingSchema,
          mapPath: (p) => p,
        }
      );
    }

    // Run validation
    for (const { name, data, schema, mapPath } of validations) {
      try {
        await schema.validate(data, { abortEarly: false });
      } catch (err) {
        err.inner.forEach((e) => {
          collectedErrors[name][mapPath(e.path)] = e.message;
        });
      }
    }

    setErrors(collectedErrors);

    const hasErrors = Object.values(collectedErrors).some(
      (obj) => Object.keys(obj).length > 0
    );

    if (hasErrors) return;

    // Build FormData
    const formData = buildShippingFormData({
      mode,
      billing,
      pickup,
      delivery,
      transport,
    });

    // Dispatch the thunk for saving data
    try {
      const orderId = "80"; // Use dynamic order ID as needed
      const response = await dispatch(
        orderShippingDetails({ id: orderId, data: formData })
      ).unwrap();
      console.log("Shipping details saved:", response);
    } catch (err) {
      console.error("Shipping API failed:", err);
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ p: 4 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "800", fontSize: "36px" }}
        >
          Shipping & Address Configuration
        </Typography>

        <ShippingModeSelector mode={mode} setMode={setMode} />
        <ImportantNotes />

        <Fade in key={mode} timeout={300}>
          <Grid container spacing={3} direction="column">
            {showPickup && (
              <Grid item xs={12} key={pickup.assistanceAvailable}>
                <PickupAddressForm
                  pickup={pickup}
                  mode={mode}
                  onChange={handleChange(setPickup, "pickup")}
                  onToggle={handleToggle(setPickup)}
                  errors={errors.pickup}
                />
              </Grid>
            )}

            {showDelivery && (
              <Grid item xs={12}>
                <DeliveryAddressForm
                  delivery={delivery}
                  onChange={handleChange(setDelivery, "delivery")}
                  onToggle={handleToggle(setDelivery)}
                  errors={errors.delivery}
                />
              </Grid>
            )}

            {mode === "third" && (
              <Grid item xs={12}>
                <TransportDocumentsForm
                  documents={transport.transportDocuments}
                  trackingDetails={transport.trackingDetails}
                  setTransport={setTransport}
                  errors={errors.transport}
                />
              </Grid>
            )}
          </Grid>
        </Fade>

        <Box sx={{ mt: 4 }}>
          <BillingAddressForm
            billing={billing}
            onChange={handleChange(setBilling, "billing")}
            onToggle={handleToggle(setBilling)}
            errors={errors.billing}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
            marginRight: "50px",
          }}
        >
          <AuthButton
            variant="outlined"
            fullWidth={false}
            style={{ minWidth: 150 }}
            onClick={() => {
              setMode("self");
              setBilling(defaultAddress());
              setPickup(defaultAddress());
              setDelivery(defaultAddress());
              setTransport(defaultAddress());
              setErrors({
                billing: {},
                pickup: {},
                delivery: {},
                transport: {},
              });
            }}
          >
            Reset
          </AuthButton>

          <AuthButton
            variant="contained"
            fullWidth={false}
            style={{ minWidth: 150 }}
            onClick={submit}
          >
            Save
          </AuthButton>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
